import React, { useEffect, useState } from "react";
import { X, Download, Smartphone, Share2, PlusSquare } from "lucide-react";

/**
 * PWA install prompt banner.
 *
 * Behaviour:
 *  - Chromium (Android/Desktop): listens for `beforeinstallprompt`, offers a
 *    native install prompt when the user taps "Install".
 *  - iOS Safari: no native API — shows a mini tutorial ("Tap the Share icon
 *    then 'Add to Home Screen'").
 *  - Skipped when the app is already installed (running in standalone mode)
 *    or when the user has dismissed within the last 14 days.
 *  - Only appears on small screens so we don't nag desktop users.
 */
const SNOOZE_KEY = "zoodPwaSnoozeUntil";
const SNOOZE_DAYS = 14;

function isStandalone() {
  return (
    (typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIPhoneIPad = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS reports as Mac + touch
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isIPhoneIPad || isIPadOS;
}

function isSnoozed() {
  try {
    const until = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10);
    return Date.now() < until;
  } catch { return false; }
}

function snooze(days = SNOOZE_DAYS) {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + days * 86400 * 1000));
  } catch { /* ignore */ }
}

export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);

  // small-screen detection lives on the DOM so we don't crash SSR.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;                // already installed
    if (isSnoozed()) return;                   // dismissed recently
    const isSmall = window.matchMedia("(max-width: 900px)").matches;
    const ios = isIOS();
    if (!isSmall && !ios) return;              // don't bug desktop users

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // slight delay so the app has a chance to paint first
      setTimeout(() => setVisible(true), 1200);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS: no native event — show manual instructions after a short delay
    if (ios) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }

    const onInstalled = () => { setVisible(false); snooze(365); };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { snooze(); setVisible(false); setShowIosSteps(false); };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") snooze(365);
        else snooze(); // 14-day snooze on decline
      } catch { /* ignore */ }
      setDeferred(null);
      setVisible(false);
    } else if (isIOS()) {
      setShowIosSteps(true);
    }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Install ZoodLeap"
      data-testid="pwa-install-banner"
      className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-md rounded-3xl border border-[#E5DECF] bg-white shadow-[0_20px_50px_rgba(31,41,55,0.18)]">
        {showIosSteps ? (
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#17BEBB]/15 text-[#0D9488]"><Smartphone size={18} /></div>
                <div className="font-heading font-extrabold text-[#1F2937]">Add ZoodLeap to your Home Screen</div>
              </div>
              <button onClick={dismiss} data-testid="pwa-banner-close" className="rounded-xl p-1 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={18} /></button>
            </div>
            <ol className="mt-3 space-y-2 font-body text-sm text-[#1F2937]">
              <li className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#0D9488] text-xs font-bold text-white">1</span>
                Tap the <Share2 size={16} className="inline text-[#0D9488]" /> <strong>Share</strong> icon in Safari
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#0D9488] text-xs font-bold text-white">2</span>
                Scroll and tap <PlusSquare size={16} className="inline text-[#0D9488]" /> <strong>Add to Home Screen</strong>
              </li>
              <li className="flex items-center gap-2">
                <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#0D9488] text-xs font-bold text-white">3</span>
                Tap <strong>Add</strong> — ZoodLeap now opens like a real app!
              </li>
            </ol>
            <button onClick={dismiss} data-testid="pwa-ios-done"
              className="mt-4 w-full rounded-full bg-[#0D9488] px-5 py-2.5 font-heading font-bold text-white hover:bg-[#0B7F74]">
              Got it
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 sm:p-4">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#17BEBB]/15">
              <img src="/brand/mascot.png" alt="" className="h-9 w-9 rounded-xl object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm font-extrabold text-[#1F2937] leading-tight">Add ZoodLeap to your Home Screen</div>
              <div className="mt-0.5 font-body text-xs text-[#6B7280] leading-snug">Learn faster with a full-screen app icon — works offline too.</div>
            </div>
            <button onClick={install} data-testid="pwa-install-btn"
              className="flex-none inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] px-3.5 py-2 text-xs font-heading font-bold text-white hover:bg-[#0B7F74]">
              <Download size={14} /> Install
            </button>
            <button onClick={dismiss} data-testid="pwa-banner-close" aria-label="Dismiss"
              className="flex-none rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]">
              <X size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
